import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type {
  AstNode,
  Block,
  Doc,
  Image,
  Inline,
  Link as DjotLink,
} from '@djot/djot';
import { parseDjot } from '@/lib/djot';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { normalizeWikiTitle } from '@/lib/wikiSlug';
import { cn } from '@/lib/utils';

interface DjotContentProps {
  /** Raw Djot markup (a wiki article's `content` field, per NIP-54). */
  content: string;
  className?: string;
}

/**
 * Renders NIP-54 Djot wiki content as React elements.
 *
 * In addition to spec-compliant Djot reference links (`[Text][]`), this
 * also understands the classic double-bracket wiki syntax — `[[Article]]`
 * and `[[Article|Display text]]` — which is expanded to the equivalent
 * Djot reference link before parsing (see `@/lib/wikiLinkSyntax`).
 * Whichever form the underlying `content` string uses, the rendered link
 * behaves identically: it resolves to `/w/<normalized-d-tag>`.
 *
 * Security: this NEVER uses `dangerouslySetInnerHTML`. Every node is mapped
 * to a concrete React element with auto-escaped text content. Djot "raw"
 * blocks/inlines (`{=html}...`) are intentionally dropped rather than
 * rendered, since djot allows arbitrary raw HTML passthrough which would be
 * an XSS vector if emitted verbatim. All link/image destinations are
 * resolved through an allowlist (internal `nostr:` routes or sanitized
 * `https://` URLs only).
 */
export function DjotContent({ content, className }: DjotContentProps) {
  const doc = parseDjot(content);

  return (
    <div className={cn('break-words', className)}>
      {doc.children.map((node, i) => (
        <Fragment key={i}>{renderBlock(node, doc)}</Fragment>
      ))}
      {renderFootnotes(doc)}
    </div>
  );
}

/** Concatenates the plain-text content of a run of inline nodes (mirrors djot's internal getStringContent). */
function stringContent(nodes: AstNode[]): string {
  let out = '';
  for (const node of nodes) {
    if ('tag' in node && node.tag === 'footnote_reference') continue;
    if ('text' in node && typeof node.text === 'string') {
      out += node.text;
    } else if ('tag' in node && (node.tag === 'soft_break' || node.tag === 'hard_break')) {
      out += '\n';
    } else if ('children' in node && Array.isArray(node.children)) {
      out += stringContent(node.children as AstNode[]);
    }
  }
  return out;
}

const smartPunctuation: Record<string, string> = {
  left_single_quote: '\u2018',
  right_single_quote: '\u2019',
  left_double_quote: '\u201C',
  right_double_quote: '\u201D',
  ellipses: '\u2026',
  em_dash: '\u2014',
  en_dash: '\u2013',
};

/** Resolves a nostr: URI to an internal route path (e.g. /npub1..., /naddr1...), or undefined if unsupported. */
function nostrUriToPath(uri: string): string | undefined {
  const identifier = uri.slice('nostr:'.length);
  try {
    const decoded = nip19.decode(identifier);
    switch (decoded.type) {
      case 'npub':
      case 'nprofile':
      case 'note':
      case 'nevent':
      case 'naddr':
        return `/${identifier}`;
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

/** Resolves a link/image's destination, following explicit or heading (auto) references. */
function resolveDestination(node: DjotLink | Image, doc: Doc): string | undefined {
  if (node.destination !== undefined) return node.destination;
  if (node.reference) {
    const ref = doc.references[node.reference] ?? doc.autoReferences[node.reference];
    if (ref) return ref.destination;
  }
  return undefined;
}

function safeClass(value: string | undefined): string | undefined {
  if (!value) return undefined;
  // Class names only ever reach the DOM `class` attribute (never CSS/JS
  // context), but constrain to a sane charset defensively.
  return value.replace(/[^\w\s-]/g, '') || undefined;
}

function renderBlocks(nodes: Block[], doc: Doc): ReactNode {
  return nodes.map((node, i) => <Fragment key={i}>{renderBlock(node, doc)}</Fragment>);
}

function renderInlines(nodes: Inline[], doc: Doc): ReactNode {
  return nodes.map((node, i) => <Fragment key={i}>{renderInline(node, doc)}</Fragment>);
}

function renderBlock(node: Block, doc: Doc): ReactNode {
  switch (node.tag) {
    case 'para':
      return <p className="mb-4 leading-7">{renderInlines(node.children, doc)}</p>;

    case 'heading': {
      const level = Math.min(Math.max(node.level, 1), 6);
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const id = node.autoAttributes?.id ?? node.attributes?.id;
      const sizes: Record<number, string> = {
        1: 'text-3xl font-bold mt-8 mb-4',
        2: 'text-2xl font-bold mt-8 mb-3 border-b pb-2',
        3: 'text-xl font-semibold mt-6 mb-3',
        4: 'text-lg font-semibold mt-5 mb-2',
        5: 'text-base font-semibold mt-4 mb-2',
        6: 'text-sm font-semibold mt-4 mb-2 text-muted-foreground',
      };
      return (
        <Tag id={id} className={sizes[level]}>
          {renderInlines(node.children, doc)}
        </Tag>
      );
    }

    case 'thematic_break':
      return <hr className="my-6 border-border" />;

    case 'section':
      return <section>{renderBlocks(node.children, doc)}</section>;

    case 'div':
      return <div className={safeClass(node.attributes?.class)}>{renderBlocks(node.children, doc)}</div>;

    case 'code_block':
      return (
        <pre className="mb-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
          <code className={node.lang ? `language-${safeClass(node.lang)}` : undefined}>{node.text}</code>
        </pre>
      );

    case 'raw_block':
      // Intentionally dropped — see security note on DjotContent.
      return null;

    case 'block_quote':
      return (
        <blockquote className="mb-4 border-l-4 border-border pl-4 italic text-muted-foreground">
          {renderBlocks(node.children, doc)}
        </blockquote>
      );

    case 'bullet_list':
      return <ul className="mb-4 list-disc space-y-1 pl-6">{node.children.map((li, i) => <li key={i}>{renderBlocks(li.children, doc)}</li>)}</ul>;

    case 'ordered_list':
      return (
        <ol className="mb-4 list-decimal space-y-1 pl-6" start={node.start}>
          {node.children.map((li, i) => <li key={i}>{renderBlocks(li.children, doc)}</li>)}
        </ol>
      );

    case 'task_list':
      return (
        <ul className="mb-4 space-y-1 pl-2">
          {node.children.map((li, i) => (
            <li key={i} className="flex items-start gap-2">
              <input type="checkbox" disabled checked={li.checkbox === 'checked'} className="mt-1.5" />
              <span>{renderBlocks(li.children, doc)}</span>
            </li>
          ))}
        </ul>
      );

    case 'definition_list':
      return (
        <dl className="mb-4 space-y-2">
          {node.children.map((item, i) => (
            <Fragment key={i}>
              <dt className="font-semibold">{renderInlines(item.children[0].children, doc)}</dt>
              <dd className="pl-4 text-muted-foreground">{renderBlocks(item.children[1].children, doc)}</dd>
            </Fragment>
          ))}
        </dl>
      );

    case 'table':
      return (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            {node.children[0].children.length > 0 && (
              <caption className="mb-2 text-muted-foreground">
                {renderInlines(node.children[0].children, doc)}
              </caption>
            )}
            <tbody>
              {node.children.slice(1).map((row, i) => (
                <tr key={i} className="border-b border-border">
                  {row.children.map((cell, j) => {
                    const style = cell.align !== 'default' ? { textAlign: cell.align } : undefined;
                    return cell.head ? (
                      <th key={j} style={style} className="p-2 text-left font-semibold">
                        {renderInlines(cell.children, doc)}
                      </th>
                    ) : (
                      <td key={j} style={style} className="p-2">
                        {renderInlines(cell.children, doc)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

function renderInline(node: Inline, doc: Doc): ReactNode {
  switch (node.tag) {
    case 'str':
      return node.text;

    case 'soft_break':
      return ' ';

    case 'hard_break':
      return <br />;

    case 'non_breaking_space':
      return '\u00A0';

    case 'symb':
      return `:${node.alias}:`;

    case 'verbatim':
      return <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{node.text}</code>;

    case 'raw_inline':
      // Intentionally dropped — see security note on DjotContent.
      return null;

    case 'inline_math':
      return <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{`\\(${node.text}\\)`}</code>;

    case 'display_math':
      return <code className="block rounded bg-muted px-1.5 py-0.5 text-sm">{`\\[${node.text}\\]`}</code>;

    case 'url': {
      const href = sanitizeUrl(node.text);
      return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline break-all">
          {node.text}
        </a>
      ) : (
        node.text
      );
    }

    case 'email':
      return (
        <a href={`mailto:${encodeURIComponent(node.text)}`} className="text-primary underline underline-offset-2 hover:no-underline">
          {node.text}
        </a>
      );

    case 'footnote_reference': {
      const index = Object.keys(doc.footnotes).indexOf(node.text) + 1;
      if (index <= 0) return null;
      return (
        <sup>
          <a href={`#fn-${index}`} id={`fnref-${index}`} className="text-primary">
            [{index}]
          </a>
        </sup>
      );
    }

    case 'smart_punctuation':
      return smartPunctuation[node.type] ?? node.text;

    case 'double_quoted':
      return (
        <>
          {smartPunctuation.left_double_quote}
          {renderInlines(node.children, doc)}
          {smartPunctuation.right_double_quote}
        </>
      );

    case 'single_quoted':
      return (
        <>
          {smartPunctuation.left_single_quote}
          {renderInlines(node.children, doc)}
          {smartPunctuation.right_single_quote}
        </>
      );

    case 'emph':
      return <em>{renderInlines(node.children, doc)}</em>;

    case 'strong':
      return <strong>{renderInlines(node.children, doc)}</strong>;

    case 'mark':
      return <mark className="rounded bg-yellow-200/60 px-0.5 dark:bg-yellow-500/30">{renderInlines(node.children, doc)}</mark>;

    case 'insert':
      return <ins>{renderInlines(node.children, doc)}</ins>;

    case 'delete':
      return <del>{renderInlines(node.children, doc)}</del>;

    case 'superscript':
      return <sup>{renderInlines(node.children, doc)}</sup>;

    case 'subscript':
      return <sub>{renderInlines(node.children, doc)}</sub>;

    case 'span':
      return <span className={safeClass(node.attributes?.class)}>{renderInlines(node.children, doc)}</span>;

    case 'image': {
      const dest = resolveDestination(node, doc);
      const src = sanitizeUrl(dest);
      const alt = stringContent(node.children);
      if (!src) return alt ? <span className="text-muted-foreground">[{alt}]</span> : null;
      return <img src={src} alt={alt} className="mx-auto my-4 max-w-full rounded-lg" loading="lazy" />;
    }

    case 'link': {
      const dest = resolveDestination(node, doc);
      const children = renderInlines(node.children, doc);

      if (dest !== undefined) {
        if (dest.startsWith('nostr:')) {
          const path = nostrUriToPath(dest);
          return path ? <Link to={path} className="text-primary underline underline-offset-2 hover:no-underline">{children}</Link> : <>{children}</>;
        }
        const href = sanitizeUrl(dest);
        return href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline">
            {children}
          </a>
        ) : (
          <>{children}</>
        );
      }

      // Unresolved reference — treat as a NIP-54 wikilink to the article
      // whose `d` tag matches the (normalized) reference label.
      const label = node.reference ?? stringContent(node.children);
      const slug = normalizeWikiTitle(label);
      if (!slug) return <>{children}</>;
      return (
        <Link to={`/w/${encodeURIComponent(slug)}`} className="text-primary underline decoration-dotted underline-offset-2 hover:no-underline">
          {children}
        </Link>
      );
    }

    default:
      return null;
  }
}

function renderFootnotes(doc: Doc): ReactNode {
  const labels = Object.keys(doc.footnotes);
  if (labels.length === 0) return null;

  return (
    <section className="mt-8 border-t border-border pt-4">
      <hr className="mb-4 border-border" />
      <ol className="space-y-2 text-sm text-muted-foreground">
        {labels.map((label, i) => {
          const note = doc.footnotes[label];
          return (
            <li key={label} id={`fn-${i + 1}`}>
              {renderBlocks(note.children, doc)}{' '}
              <a href={`#fnref-${i + 1}`} className="text-primary">
                ↩
              </a>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
